import type { HTMLAttributes } from 'react';

import type {
  GetWebcastStreamNewAuthQuery,
  GetWebcastViewerNewAuthQuery,
  Webcast,
} from '../../../generated/graphql-viewer';

import { CalendarSmallIcon, Heading, Paragraph, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextContent } from '../../../components/rich-text';
import { State } from '../../../generated/graphql-viewer';
import { getLocale } from '../../../i18n';
import { formatEventStartDate, isRichTextEditorContentEmpty } from '../../../utils';
import { useSelectedLanguage } from '../../providers/selected-language-provider';
import { ComplianceLinks } from '../compliance-links';
import { getCounterText } from '../get-counter-text';
import { Speakers } from '../speakers';

import styles from './content.module.css';

type Props = {
  webcast: GetWebcastStreamNewAuthQuery['webcastNewAuth'] & GetWebcastViewerNewAuthQuery['webcastNewAuth'];
  language?: string;
  highlightedSpeakerId?: string;
} & HTMLAttributes<HTMLDivElement>;

export function Content({ webcast, className, language, highlightedSpeakerId, ...props }: Props) {
  const { t } = useTranslation();
  const { primaryLanguage, isCustomLanguage } = useSelectedLanguage();

  const _language = isCustomLanguage(language) ? primaryLanguage?.code : language;
  const locale = getLocale(_language);

  const [startTimeCounter, setStartTimeCounter] = useState('');

  const webcastState = webcast?.state || State.PRELIVE;
  const webcastIsLive = webcastState === State.LIVE;

  const updateCounter = useCallback(
    (plannedStartAt: Webcast['plannedStartAt'], state: Webcast['state']) => {
      const startDate = new Date(plannedStartAt);
      const now = new Date();
      const counterText = getCounterText(startDate, now, state, locale, t);

      setStartTimeCounter(counterText);
    },
    [locale, t]
  );

  useEffect(() => {
    if (!webcast?.plannedStartAt) return;

    updateCounter(webcast.plannedStartAt, webcastState);

    const interval = setInterval(() => updateCounter(webcast.plannedStartAt, webcastState), 1000);

    return () => clearInterval(interval);
  }, [webcast?.plannedStartAt, webcastState, updateCounter]);

  const currentLanguageContent = webcast?.contents.find((content) => content.language === _language);
  const primaryLanguageContent = webcast?.contents.find((content) => content.language === webcast?.primaryLang.code);
  const primaryLanguageTitle = primaryLanguageContent?.title;
  const title = webcast?.contents.find((content) => content.language === language)?.title || primaryLanguageTitle;

  const content = {
    [State.PRELIVE]: currentLanguageContent?.preLive?.description,
    [State.LIVE]: currentLanguageContent?.live?.description,
    [State.LIVE_TEST]: currentLanguageContent?.live?.description,
    [State.POSTLIVE]: currentLanguageContent?.postLive?.description,
  }[webcastState];

  const contentAvailable = !isRichTextEditorContentEmpty(content);

  if (!webcast) return null;

  return (
    <div className={classNames(styles.wrapper, className)} {...props}>
      {!webcastIsLive && startTimeCounter && !webcast.hideWebcastSchedule && (
        <div
          className={classNames(styles.stateBadge, webcastState === State.POSTLIVE && styles.outro)}
          data-testid="event-state-badge"
        >
          {startTimeCounter}
          {webcastState === State.PRELIVE ? <CalendarSmallIcon /> : <span className={styles.dot} />}
        </div>
      )}

      {!webcast.hideWebcastSchedule && (
        <Paragraph data-testid="event-date">
          {formatEventStartDate(webcast.plannedStartAt, webcast.durationInMinutes)}
        </Paragraph>
      )}

      <div className={styles.body}>
        <Heading data-testid="event-title">{title}</Heading>
        {contentAvailable && <RichTextContent data-testid="event-content-rich-text-editor" value={content} />}
        <Speakers speakers={webcast.speakers} highlightedSpeakerId={highlightedSpeakerId} />
        <ComplianceLinks complianceForms={webcast.complianceForms} />
      </div>
    </div>
  );
}
